import { FC, FocusEvent, useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";

import { Button, ButtonAppearance, Buttons, Link, Tooltip } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Dashboard } from "capitalrx-types";
import classNames from "classnames";
import styled from "styled-components";

import { useLayout } from "../../ContentLayout";
import { useContentChannel } from "../../components/ContentChannel";
import { useShellModulePathResolver } from "../../hooks";
import { Actions } from "./Actions";
import { Logo } from "./Logo";
import { Menu } from "./Menu";
import { Notifications } from "./Notifications";
import { getNavigationGroupClassName } from "./getNavigationGroupClassName";
import { useMenuGroups, useMenuItems } from "./hooks";

const MenuButtonAppearance: ButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,

  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.White,
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.Turqoise["100"],
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.White,
  },
};

const ExpandedMenuButtonAppearance: ButtonAppearance = {
  ...MenuButtonAppearance,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.Turqoise["100"],
  },
};

const Root = styled.nav`
  font-size: 14px;
  line-height: 24px;
  color: ${Color.toString(Color.DarkGrey[600])};
  height: 3.5rem;

  .menu-button {
    button {
      height: 100%;
    }

    &:focus {
      outline: none;
    }
  }

  .bar {
    height: 3.5rem;
    width: 100%;
    padding: 0 1rem;
    background: linear-gradient(270deg, #254591 0%, #0059b3 100%);
    border-bottom: solid 1px ${Color.toString(Color.LightGrey[200])};
  }

  .module-name {
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 600;
    line-height: 0.875rem;
    text-transform: capitalize;
    color: ${Color.toString(Color.White)};
    cursor: default;
  }

  & > .bar-container {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 90000000;
    box-sizing: border-box;

    &:focus {
      outline: none;
    }

    & > .menu {
      position: absolute;
      transform: translateY(-100%);
      background-color: ${Color.toString(Color.White)};
      transition: transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s;
      width: 100%;
      left: 0;
      top: 3.5rem;
      z-index: -1;
      padding: 1.5rem;
      box-shadow: 0px 0px 8px transparent;
      transition:
        transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s,
        box-shadow 0.5s linear 0.3s;
      overflow-y: auto;
      max-height: calc(100vh - 3.5rem);

      &:focus {
        outline: none;
      }

      &.expanded {
        transform: translateY(0);
        box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.16);
        transition:
          transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s,
          box-shadow 0s linear 0s;
      }
    }
  }
`;

const MenuGrid = styled(Grid)`
  width: 100%;
  column-gap: 2rem;

  .column {
    & > .group {
      & > .header {
        font-family: "Source Sans 3";
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 600;
        line-height: 0.875rem;
        letter-spacing: 0.125rem;
        text-transform: uppercase;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid ${Color.toString(Color.LightGrey[200])};
        cursor: default;
      }
    }
  }

  .link {
    color: ${Color.toString(Color.DarkGrey[500])} !important;
    text-decoration: none;
    line-height: 1.5rem;

    &:hover {
      color: ${Color.toString(Color.Turqoise[500])} !important;
      text-decoration: underline;
    }
  }

  .beta {
    display: flex;
    height: 0.75rem;
    padding: 0rem 0.125rem;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    border-radius: 0.125rem;
    background: #60646d;
    color: white;
    font-size: 0.5625rem;
    font-style: normal;
    font-weight: 700;
    line-height: 0.875rem;
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    cursor: default;
  }
`;

/**
 * The shell main menu bar component.
 */
export const MenuBar: FC = () => {
  const [expanded, setExpanded] = useState(false);
  const buttonContainerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const resolvePath = useShellModulePathResolver();
  const actionBars = useContentChannel(null, "app-action-bars");
  const layoutType = useLayout();

  const { menuColumnSpan, nameVisible } = useResponsive({
    menuColumnSpan: { desktop: 3, tablet: 6, mobile: 12 },
    nameVisible: { desktop: true, tablet: true, mobile: false },
  });

  const items = useMenuItems();
  const columnGroups = useMenuGroups(items, 12 / menuColumnSpan);

  const clickHandler = useCallback(() => {
    setExpanded((previousExpanded) => !previousExpanded);

    if (buttonContainerRef.current) {
      buttonContainerRef.current.focus(); // Ensures the button gets the focus.
    }
  }, []);

  const mouseDownHandler = useCallback((ev: React.MouseEvent): void => {
    ev.preventDefault();
  }, []);

  const blurHandler = useCallback((ev: FocusEvent): void => {
    const { relatedTarget } = ev;
    if (relatedTarget) {
      if (relatedTarget === buttonContainerRef?.current) return;
      if (relatedTarget === menuRef?.current || (menuRef.current && menuRef.current.contains(relatedTarget as Node))) return;
    }

    setExpanded(false);
  }, []);

  useEffect(() => {
    function listener(): void {
      setExpanded(false);
    }

    if (expanded) {
      document.addEventListener("scroll", listener);
      window.addEventListener("resize", listener);
    }

    return (): void => {
      document.removeEventListener("scroll", listener);
      window.removeEventListener("resize", listener);
    };
  }, [expanded]);

  useEffect(() => {
    setExpanded(false);
  }, [location]);

  if (layoutType.startsWith("fullscreen")) {
    return null;
  }

  const columnElements = columnGroups.map((groups, columnIndex) => {
    return (
      <Grid.Span key={columnIndex} columns={menuColumnSpan}>
        <Stack className="column" key={columnIndex} spacing="large">
          {groups.map((group) => {
            const groupData = Dashboard.NavigationGroup[group.id] ?? Dashboard.NavigationGroup.Unknown;

            return (
              <Stack className={classNames("group", getNavigationGroupClassName(groupData))} key={groupData.id} spacing="mini">
                <h3 className="header" style={{ color: groupData.color }}>
                  {groupData.name}
                </h3>
                <Stack spacing="mini">
                  {group.items.map((item, index) => {
                    const { module, data } = item;
                    const { path, testId, title, beta = false, absolutePath = false } = data;

                    const resolvedPath = absolutePath ? path : resolvePath(module, path);

                    return (
                      <Shelf key={index} spacing="mini">
                        <Link to={resolvedPath} testId={testId} tabIndex={2}>
                          {title}
                        </Link>
                        {beta && <span className="beta">BETA</span>}
                        <Shelf.Spacer />
                      </Shelf>
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Grid.Span>
    );
  });

  return (
    <Root data-testid="menubar" className={classNames("menu-bar", { "has-action-bars": actionBars.length > 0 })}>
      <div className="bar-container" tabIndex={-1} onBlur={blurHandler}>
        <Shelf className="bar" alignment="center">
          <div
            className="menu-button"
            ref={buttonContainerRef}
            onClickCapture={clickHandler}
            onBlurCapture={blurHandler}
            onMouseDown={mouseDownHandler}
            tabIndex={1}
          >
            <Button icon="menu-grid" appearance={expanded ? ExpandedMenuButtonAppearance : MenuButtonAppearance} tabIndex={-1} />
          </div>
          <Logo />
          {nameVisible && (
            <Shelf alignment="span">
              <Routes>
                {items.map((item, index) => {
                  const { data, module } = item;
                  const { title, description, path, absolutePath = false } = data;

                  let resolvedPath = path;
                  if (resolvedPath && resolvedPath.startsWith("~")) resolvedPath = resolvedPath.substring(1);
                  resolvedPath = absolutePath ? resolvedPath : (resolvePath(module, resolvedPath) ?? resolvePath(module, "/"));
                  resolvedPath += resolvedPath.endsWith("/") ? "*" : "/*";

                  return (
                    <Route
                      key={index}
                      path={resolvedPath}
                      element={
                        <Tooltip content={description} alignment="bottom">
                          {(ref) => (
                            <div className="module-name" ref={ref}>
                              {title}
                            </div>
                          )}
                        </Tooltip>
                      }
                    />
                  );
                })}
                <Route path="*" element={null} />
              </Routes>
            </Shelf>
          )}
          <Shelf.Spacer />
          <Shelf spacing="none">
            <Notifications />
            <Actions />
          </Shelf>
        </Shelf>

        <Menu ref={menuRef} expanded={expanded}>
          <MenuGrid spacing="none">{columnElements}</MenuGrid>
        </Menu>
      </div>
    </Root>
  );
};
